import { Button, ButtonStyle } from '@atlas/components';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  type TransactionHistoryRequest,
  getTransactionHistory,
} from 'api/payments';
import DateRange from 'components/DateRange';
import ErrorEmptyState from 'components/EmptyStates/ErrorEmptyState';
import AccountLayout from 'components/Layouts/Account';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import { TransactionTable } from 'components/TransactionsTable';
import { useClientProfile } from 'store';
import useLayout, { LayoutType } from 'utils/layout';
import { millisecondsIn89Days } from 'utils/time';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useSliceRequest from 'utils/useSliceRequest';

import styles from './TransactionsPage.module.scss';

const dateNow = new Date();

const TransactionsPage = () => {
  const [{ sessionToken }] = useClientProfile();
  const [dateFrom, setDateFrom] = useState<Date>(() => {
    const now = new Date();

    const from = new Date(now.getTime() - millisecondsIn89Days);
    from.setHours(0, 0, 0, 0); // Ensure that this is the start of the day

    return from;
  });
  const [dateTo, setDateTo] = useState<Date>(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);

    return now;
  });
  const layout = useLayout();
  const isDesktop = layout === LayoutType.tablet || layout === LayoutType.wide;

  const transactionsProps: TransactionHistoryRequest = useMemo(
    () => ({
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
      query: {
        to: dateTo.getTime(),
        from: dateFrom.getTime(),
      },
    }),
    [sessionToken, dateFrom, dateTo],
  );

  const { data, loading, loadMore, hasNext, error } = useSliceRequest(
    getTransactionHistory,
    transactionsProps,
  );

  const [sentryRef] = useInfiniteScroll({
    disabled: !!error || !hasNext || isDesktop,
    hasNextPage: hasNext,
    loading: loading === LoadingState.loading,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  const isDisplayShowMoreButton =
    loading === LoadingState.success && isDesktop && hasNext;

  const handleChangeDateFrom = useCallback((date: Date | null) => {
    if (date) {
      setDateFrom(date);
    }
  }, []);

  const handleChangeDateTo = useCallback((date: Date | null) => {
    if (date) {
      setDateTo(() => {
        /**
         * If the selected date is earlier than the current date, its time is set to 23:59:59.999
         * to avoid interpreting it as "previous."
         * If the date is later or equal to the current date, it is saved unchanged.
         */
        if (date < dateNow) {
          date.setHours(23, 59, 59, 999);

          return date;
        }

        return date;
      });
    }
  }, []);

  return (
    <AccountLayout icon="empty-wallet-time" title="transactions">
      <Meta title={<Translation message="title_transactions" />} />
      <DateRange
        dateFrom={dateFrom}
        dateTo={dateTo}
        handleChangeDateFrom={handleChangeDateFrom}
        handleChangeDateTo={handleChangeDateTo}
      />
      <TransactionTable
        transactions={data}
        isLoaded={loading === LoadingState.success}
      />
      {loading === LoadingState.loading && <Loader />}
      {loading === LoadingState.failed && (
        <div
          className={classNames(
            styles['transactions-page-error'],
            styles[layout],
          )}
        >
          <ErrorEmptyState />
        </div>
      )}
      {!isDesktop && (
        <div className={styles['transactions-sentinel']} ref={sentryRef} />
      )}
      {isDisplayShowMoreButton && (
        <Button
          style={ButtonStyle.outline}
          className={styles['transactions-show-more']}
          onClick={loadMore}
        >
          <Translation message="show_more" />
        </Button>
      )}
    </AccountLayout>
  );
};

export default TransactionsPage;
